import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { EBaseUrl } from "index";
import thunkOptions from "store/config/thunkOptions";
import {
  IAddCampaignTranslation,
  IAutotranslateCampaign,
  IAutoTranslateLanguage,
  ICampaignTranslation,
  ICampaignTranslationInitialState,
  IDeleteCampaignTranslation,
  IMakeCampaignTranslationVisible,
  ITranslationLanguage,
} from "store/interfaces/campaignTranslation";
import { IPaginated, IState } from "store/interfaces/main";
import { api } from "../services/apiService";

const name = "CAMPAIGNS";

const initialState: ICampaignTranslationInitialState = {
  gridData: null,
  languages: [],
  autoTranslateLanguages: [],
};

export const GetCampaignTranslationGrid = createAsyncThunk<
  IPaginated<ICampaignTranslation>,
  string
>(
  `${name}/GetCampaignTranslationGrid`,
  async (id) => {
    return (
      await api.get(
        `${EBaseUrl.API}/Campaign/CampaignTranslationOverview/${id}`
      )
    ).data;
  },
  thunkOptions
);

export const AutoTranslateCampaign = createAsyncThunk<
  any,
  IAutotranslateCampaign
>(
  `${name}/AutoTranslateCampaign`,
  async ({ campaignId, languageCode, ToLanguageCodeAlpha }) => {
    return (
      await api.put(
        `${EBaseUrl.API}/Campaign/AutotranslateCampaign?CampaignID=${campaignId}&ToLanguageCode=${languageCode}&ToLanguageCodeAlpha=${ToLanguageCodeAlpha}`
      )
    ).data;
  },
  thunkOptions
);

export const GetAutoTranslateLanguageList = createAsyncThunk<
  IAutoTranslateLanguage[]
>(
  `${name}/GetAutoTranslateLanguageList`,
  async () => {
    return (await api.get(`${EBaseUrl.API}/Campaign/LanguagesList`)).data;
  },
  thunkOptions
);

export const GetTranslationLanguages = createAsyncThunk<ITranslationLanguage[]>(
  `${name}/GetTranslationLanguages`,
  async () => {
    return (
      await api.get(`${EBaseUrl.API}/Campaign/GetCampaignTranslationLanguages`)
    ).data;
  },
  thunkOptions
);

export const AddTranslationForCampaign = createAsyncThunk<
  any,
  IAddCampaignTranslation
>(
  `${name}/AddTranslationForCampaign`,
  async (body) => {
    return (
      await api.post(`${EBaseUrl.API}/Campaign/AddCampaignTranslation`, body)
    ).data;
  },
  thunkOptions
);

export const DeleteCampaignTranslation = createAsyncThunk<
  any,
  IDeleteCampaignTranslation
>(
  `${name}/DeleteCampaignTranslation`,
  async ({ id, languageID }) => {
    return (
      await api.delete(
        `${EBaseUrl.API}/Campaign/DeleteCampaignTranslation/${id}/${languageID}`
      )
    ).data;
  },
  thunkOptions
);

export const MakeCampaignTranslationVisible = createAsyncThunk<
  any,
  IMakeCampaignTranslationVisible
>(
  `${name}/MakeCampaignTranslationVisible`,
  async ({ campaignId, languageId }) => {
    return (
      await api.put(
        `${EBaseUrl.API}/Campaign/ChangeCampaignTranslationVisabilityForRespondents?LanguageId=${languageId}&CampaignId=${campaignId}`
      )
    ).data;
  },
  thunkOptions
);

const campaignTranslationSlicer = createSlice({
  initialState,
  name,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      GetCampaignTranslationGrid.fulfilled,
      (state, { payload }) => {
        state.gridData = payload;
      }
    );
    builder.addCase(GetTranslationLanguages.fulfilled, (state, { payload }) => {
      state.languages = payload;
    });
    builder.addCase(
      GetAutoTranslateLanguageList.fulfilled,
      (state, { payload }) => {
        state.autoTranslateLanguages = payload;
      }
    );
  },
});

export const selectCampaignTranslations = (state: IState) =>
  state.campaignTranslation.gridData;

export const selectTranslationLanguages = (state: IState) =>
  state.campaignTranslation.languages;

export const selectAutotranslateLanguages = (state: IState) =>
  state.campaignTranslation.autoTranslateLanguages;

export default campaignTranslationSlicer.reducer;
